import React, { useState, useEffect, useMemo } from 'react';
import { getLlmUsageStats, getLlmUsageLogs } from '../../../utils/apiClient';
import { Line, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import {
    CurrencyDollarIcon,
    ChartBarIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    ClockIcon,
    CpuChipIcon,
} from '@heroicons/react/24/outline';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
);

const LlmUsageDashboard = () => {
    const [stats, setStats] = useState(null);
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [daysFilter, setDaysFilter] = useState(30);

    /**
     * Normalize backend response into the shape the dashboard expects.
     * Backend returns: { totals: {...}, by_provider: [{provider, ...}], by_task: [{task, ...}], daily: [...] }
     * Dashboard expects: { total_requests, by_provider: {NAME: {...}}, by_task: {NAME: {...}}, daily_trend: [...] }
     */
    const normalizeStats = (raw) => {
        if (!raw) return raw;
        const totals = raw.totals || {};

        // Convert by_provider list to dict keyed by provider name
        const byProviderDict = {};
        if (Array.isArray(raw.by_provider)) {
            raw.by_provider.forEach((item) => {
                byProviderDict[item.provider || 'Unknown'] = {
                    requests: item.requests || 0,
                    tokens: item.tokens || 0,
                    cost: item.cost ? parseFloat(item.cost) : 0,
                };
            });
        } else if (raw.by_provider && typeof raw.by_provider === 'object') {
            Object.assign(byProviderDict, raw.by_provider);
        }

        // Convert by_task list to dict keyed by task name
        const byTaskDict = {};
        if (Array.isArray(raw.by_task)) {
            raw.by_task.forEach((item) => {
                byTaskDict[item.task || 'unknown'] = {
                    requests: item.requests || 0,
                    tokens: item.tokens || 0,
                    cost: item.cost ? parseFloat(item.cost) : 0,
                };
            });
        } else if (raw.by_task && typeof raw.by_task === 'object') {
            Object.assign(byTaskDict, raw.by_task);
        }

        const totalReqs = totals.requests ?? raw.total_requests ?? 0;
        const successfulReqs = totals.success_rate ?? raw.success_rate ?? 100;
        const fallbackRate = totals.fallback_rate ?? raw.fallback_rate ?? 0;

        return {
            total_requests: totalReqs,
            total_tokens: totals.total_tokens ?? raw.total_tokens ?? 0,
            total_cost: parseFloat(totals.total_cost_usd ?? raw.total_cost ?? 0),
            // Backend may return 0-100; normalize to 0-1 for the dashboard
            success_rate: successfulReqs > 1 ? successfulReqs / 100 : successfulReqs,
            avg_latency_ms: totals.avg_latency_ms ?? raw.avg_latency_ms ?? 0,
            fallback_rate: fallbackRate > 1 ? fallbackRate / 100 : fallbackRate,
            by_provider: byProviderDict,
            by_task: byTaskDict,
            daily_trend: raw.daily || raw.daily_trend || [],
        };
    };

    const fetchData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [statsData, logsData] = await Promise.all([
                getLlmUsageStats({ days: daysFilter }),
                getLlmUsageLogs({ limit: 50 }),
            ]);
            setStats(normalizeStats(statsData));
            setLogs(logsData || []);
        } catch (err) {
            setError(err.message || 'Failed to load LLM usage data');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [daysFilter]);

    // Stats cards data
    const summaryCards = useMemo(() => {
        if (!stats) return [];
        return [
            {
                title: 'Total Requests',
                value: stats.total_requests?.toLocaleString() || '0',
                icon: ChartBarIcon,
                color: 'bg-blue-500',
            },
            {
                title: 'Total Tokens',
                value: stats.total_tokens?.toLocaleString() || '0',
                icon: CpuChipIcon,
                color: 'bg-purple-500',
            },
            {
                title: 'Estimated Cost',
                value: `$${(stats.total_cost || 0).toFixed(4)}`,
                icon: CurrencyDollarIcon,
                color: 'bg-green-500',
            },
            {
                title: 'Success Rate',
                value: `${((stats.success_rate || 0) * 100).toFixed(1)}%`,
                icon: stats.success_rate >= 0.95 ? CheckCircleIcon : ExclamationCircleIcon,
                color: stats.success_rate >= 0.95 ? 'bg-emerald-500' : 'bg-amber-500',
            },
            {
                title: 'Avg Latency',
                value: `${(stats.avg_latency_ms || 0).toFixed(0)}ms`,
                icon: ClockIcon,
                color: 'bg-cyan-500',
            },
            {
                title: 'Fallback Rate',
                value: `${((stats.fallback_rate || 0) * 100).toFixed(1)}%`,
                icon: ExclamationCircleIcon,
                color: stats.fallback_rate > 0.1 ? 'bg-red-500' : 'bg-gray-500',
            },
        ];
    }, [stats]);

    // Provider breakdown doughnut chart
    const providerChartData = useMemo(() => {
        if (!stats?.by_provider) return null;
        const providers = Object.entries(stats.by_provider);
        if (providers.length === 0) return null;

        return {
            labels: providers.map(([name]) => name),
            datasets: [
                {
                    data: providers.map(([, data]) => data.requests || 0),
                    backgroundColor: ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b'],
                    borderWidth: 0,
                },
            ],
        };
    }, [stats]);



    // Daily trend line chart
    const trendChartData = useMemo(() => {
        if (!stats?.daily_trend) return null;
        const trends = stats.daily_trend;
        if (!Array.isArray(trends) || trends.length === 0) return null;

        return {
            labels: trends.map((t) => t.date),
            datasets: [
                {
                    label: 'Requests',
                    data: trends.map((t) => t.requests || 0),
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    fill: true,
                    tension: 0.3,
                },
                {
                    label: 'Tokens (÷1000)',
                    data: trends.map((t) => (t.tokens || 0) / 1000),
                    borderColor: '#8b5cf6',
                    backgroundColor: 'rgba(139, 92, 246, 0.1)',
                    fill: false,
                    tension: 0.3,
                },
            ],
        };
    }, [stats]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { color: '#e5e7eb' } },
        },
        scales: {
            x: {
                ticks: { color: '#94a3b8' },
                grid: { color: '#374151' },
            },
            y: {
                beginAtZero: true,
                ticks: { color: '#94a3b8' },
                grid: { color: '#374151' },
            },
        },
    };

    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { color: '#e5e7eb' } },
        },
    };

    if (isLoading && !stats) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 bg-gray-900 text-white min-h-screen">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold text-gray-100">LLM Usage Dashboard</h1>
                    <p className="text-sm text-gray-400 mt-1">Monitor token usage, costs, and API performance</p>
                </div>
                <div className="flex items-center gap-3">
                    <select
                        value={daysFilter}
                        onChange={(e) => setDaysFilter(Number(e.target.value))}
                        className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-sm text-white focus:ring-primary-500 focus:border-primary-500"
                    >
                        <option value={7}>Last 7 days</option>
                        <option value={14}>Last 14 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                    </select>
                    <button
                        onClick={fetchData}
                        disabled={isLoading}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-md text-sm font-medium disabled:bg-gray-600"
                    >
                        {isLoading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-900/30 border border-red-800 rounded-lg text-red-300">
                    {error}
                </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                {summaryCards.map((card) => (
                    <div
                        key={card.title}
                        className="bg-gray-800 rounded-lg p-4 shadow-md"
                    >
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${card.color}`}>
                                <card.icon className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-400">{card.title}</p>
                                <p className="text-lg font-semibold text-white">{card.value}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Daily Trend */}
                <div className="lg:col-span-2 bg-gray-800 rounded-lg p-4 shadow-md">
                    <h3 className="text-lg font-medium text-gray-200 mb-4">Usage Trend</h3>
                    <div style={{ height: '300px' }}>
                        {trendChartData ? (
                            <Line data={trendChartData} options={chartOptions} />
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-500">
                                No trend data available
                            </div>
                        )}
                    </div>
                </div>

                {/* Provider Breakdown */}
                <div className="bg-gray-800 rounded-lg p-4 shadow-md">
                    <h3 className="text-lg font-medium text-gray-200 mb-4">By Provider</h3>
                    <div style={{ height: '300px' }}>
                        {providerChartData ? (
                            <Doughnut data={providerChartData} options={doughnutOptions} />
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-500">
                                No provider data available
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Task Breakdown */}
            {stats?.by_task && Object.keys(stats.by_task).length > 0 && (
                <div className="bg-gray-800 rounded-lg p-4 shadow-md mb-6">
                    <h3 className="text-lg font-medium text-gray-200 mb-4">Usage by Task Type</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead>
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Task</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Requests</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Tokens</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Cost</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                                {Object.entries(stats.by_task).map(([task, data]) => (
                                    <tr key={task} className="hover:bg-gray-700/50">
                                        <td className="px-4 py-3 text-sm text-gray-300 capitalize">{task.replace(/_/g, ' ')}</td>
                                        <td className="px-4 py-3 text-sm text-gray-300 text-right">{(data.requests || 0).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-sm text-gray-300 text-right">{(data.tokens || 0).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-sm text-green-400 text-right">${(data.cost || 0).toFixed(4)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Recent Logs */}
            <div className="bg-gray-800 rounded-lg p-4 shadow-md">
                <h3 className="text-lg font-medium text-gray-200 mb-4">Recent Activity</h3>
                {logs.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead>
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Time</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Provider</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Model</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Task</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Tokens</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Cost</th>
                                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-400 uppercase">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                                {logs.slice(0, 20).map((log, idx) => (
                                    <tr key={log.id || idx} className="hover:bg-gray-700/50">
                                        <td className="px-4 py-2 text-xs text-gray-400">
                                            {new Date(log.created_at).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2 text-sm">
                                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${log.provider === 'GROQ'
                                                ? 'bg-blue-900/50 text-blue-300'
                                                : 'bg-purple-900/50 text-purple-300'
                                                }`}>
                                                {log.provider}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-xs text-gray-300 font-mono truncate max-w-[150px]" title={log.model}>
                                            {log.model}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-300 capitalize">{log.task?.replace(/_/g, ' ') || '-'}</td>
                                        <td className="px-4 py-2 text-sm text-gray-300 text-right">{(log.total_tokens || 0).toLocaleString()}</td>
                                        <td className="px-4 py-2 text-sm text-green-400 text-right">${(log.estimated_cost_usd || 0).toFixed(6)}</td>
                                        <td className="px-4 py-2 text-center">
                                            {log.success ? (
                                                <CheckCircleIcon className="h-5 w-5 text-green-500 inline" />
                                            ) : (
                                                <ExclamationCircleIcon className="h-5 w-5 text-red-500 inline" />
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-center text-gray-500 py-8">No usage logs available yet. Make some LLM calls to see data here.</p>
                )}
            </div>
        </div>
    );
};

export default LlmUsageDashboard;
